import React, {useState, useCallback} from "react";
import '../assets/styles/Transcribe.scss';
import { styled } from '@mui/material/styles';
import FadeIn from './compliance/FadeIn';
import MuiAudioPlayer from "mui-audio-player-plus";

// Import MUI elements
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Modal from '@mui/material/Modal';
import Skeleton from '@mui/material/Skeleton';
import TranscribeIcon from '@mui/icons-material/Transcribe';
import config from '../config';

// Set audio files
const wav1 = require('../assets/audio/Call1_separated_16k_health_insurance.wav');
const wav2 = require('../assets/audio/Call3_separated_16k_pharmacy_call.wav');
const wav3 = require('../assets/audio/Call6_mono_16k_az_apply_loan.wav');
const wav4 = require('../assets/audio/Two_Channel_worse.mp3');

const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 850,
    height: 600,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block'
};

const Element = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
}));

const extensionToMime: Record<string, string> = {
    flac: 'audio/flac',
    m4a: 'audio/mp4',
    mp3: 'audio/mpeg',
    mp4: 'audio/mp4',
    mpeg: 'audio/mpeg',
    mpga: 'audio/mpeg',
    oga: 'audio/ogg',
    ogg: 'audio/ogg',
    wav: 'audio/wav',
    webm: 'audio/webm',
};

const getAudioExtension = (audioPath: string): string => {
    const cleanPath = audioPath.split('?')[0];
    const fileName = cleanPath.split('/').pop() || '';
    const extension = fileName.split('.').pop() || 'wav';
    return extension.toLowerCase();
};

function Transcribe({parentToChild}: {parentToChild: any}) {

    const {mode} = parentToChild;

    const [open, setOpen] = useState(false);
    const [isTranscriptLoading, setIsTranscriptLoading] = useState(true);
    const [transcriptData, setTranscriptData] = useState<any>();

    // Get transcript data via AzureOpenAI
    const fetchData = useCallback(async (selectedAudio: string) => {
        setIsTranscriptLoading(true);
        setTranscriptData('');

        try {
            const apiKey = config.azureOpenAI.whisperKey;
            const apiUrl = config.azureOpenAI.whisperUrl;

            if (!apiKey || !apiUrl) {
                throw new Error('Whisper credentials are not configured in environment variables.');
            }

            const formData = new FormData();
            const extension = getAudioExtension(selectedAudio);
            const mimeType = extensionToMime[extension] || 'application/octet-stream';

            const audioResponse = await fetch(selectedAudio);
            if (!audioResponse.ok) {
                throw new Error('Unable to load selected audio file.');
            }

            const buffer = await audioResponse.arrayBuffer();
            const blob = new Blob([buffer], { type: mimeType });
            formData.append('file', blob, `audio.${extension}`);

            // Uncomment below for logging formData
            // for (const pair of formData.entries()) {
            //     console.log(pair[0] + ': ' + pair[1]);
            // }
            // console.log(formData)
    
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'api-key': apiKey,
                    // 'Content-Type': 'multipart/form-data',
                },
                body: formData,
            });
    
            if (!response.ok) {
                let errorMessage = `Server error: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = `Server error: ${errorData?.error?.message || errorMessage}`;
                } catch (_) {
                    // Fall back to status code if body is non-JSON.
                }
                throw new Error(errorMessage);
            }
    
            const result = await response.json();
            //console.log(result);
            setTranscriptData(result.text);
            setIsTranscriptLoading(false);
        } catch (error: any) {
            console.error('Error fetching transcript data:', error.message);
            setTranscriptData(error.message || 'Failed to transcribe audio.');
            setIsTranscriptLoading(false);
        }
    }, []);

    const handleClick = (audio: string) => {
        fetchData(audio);
    }

    const openModal = () => {
        setOpen(true);
    };
    
    const closeModal = () => {
        setOpen(false);
    };

    return (
    <FadeIn transitionDuration={700}>
        <div className='transcribe-header container'>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12} className={mode === 'dark' ? 'transcribe-dark-mode' : ''}>
                    <p>Select an audio file from the list to transcribe:</p>
                    <Element className="audio-container">
                        <List>
                            <ListItem className="audio-player">
                                <h3>Call #1</h3>
                                <MuiAudioPlayer id="custom-1" src={wav1} inline display="waveform" waveColor="#2e2e38" containerWidth="80%"/>
                                <span>
                                    <Button className="exec-nav-btn" variant="contained" endIcon={<TranscribeIcon />} onClick={() => {
                                        handleClick(wav1);
                                        openModal();
                                    }}>
                                        Transcribe
                                    </Button>
                                </span>
                            </ListItem>
                            <Divider/>
                            <ListItem className="audio-player">
                                <h3>Call #2</h3>
                                <MuiAudioPlayer id="custom-2" src={wav2} inline display="waveform" waveColor="#2e2e38" containerWidth="80%"/>
                                <span>
                                    <Button className="exec-nav-btn" variant="contained" endIcon={<TranscribeIcon />} onClick={() => {
                                        handleClick(wav2);
                                        openModal();
                                    }}>
                                        Transcribe
                                    </Button>
                                </span>
                            </ListItem>
                            <Divider/>
                            <ListItem className="audio-player">
                                <h3>Call #3</h3>
                                <MuiAudioPlayer id="custom-3" src={wav3} inline display="waveform" waveColor="#2e2e38" containerWidth="80%"/>
                                <span>
                                    <Button className="exec-nav-btn" variant="contained" endIcon={<TranscribeIcon />} onClick={() => {
                                        handleClick(wav3);
                                        openModal();
                                    }}>
                                        Transcribe
                                    </Button>
                                </span>
                            </ListItem>
                            <Divider/>
                            <ListItem className="audio-player">
                                <h3>Call #4</h3>
                                <MuiAudioPlayer id="custom-4" src={wav4} inline display="waveform" waveColor="#2e2e38" containerWidth="80%"/>
                                <span>
                                    <Button className="exec-nav-btn" variant="contained" endIcon={<TranscribeIcon />} onClick={() => {
                                        handleClick(wav4);
                                        openModal();
                                    }}>
                                        Transcribe
                                    </Button>
                                </span>
                            </ListItem>
                        </List>
                    </Element>
                </Grid>
            </Grid>
            <Modal className={mode === 'dark' ? 'transcribed-text-modal transcribe-dark-mode' : 'transcribed-text-modal'} open={open} onClose={closeModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className='modal-style' sx={ModalStyle}>
                    <h3><TranscribeIcon /> Transcribed Text</h3>
                    <div>
                        {!isTranscriptLoading ? (
                            <div>
                                <p className="transcribed-text">{transcriptData}</p>
                            </div>
                        ) : (
                                <Skeleton width="100%" height="450px" />
                        )}
                    </div>
                </Box>
            </Modal>
        </div>
    </FadeIn>
    );
}

export default Transcribe;
